import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart3, TrendingUp, TrendingDown, DollarSign, Package,
  Users, MapPin, Calendar, Download, Filter, RefreshCw,
  CheckCircle2, Truck, Clock, AlertCircle
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { api } from '@/lib/api';
import { ExportButton } from '@/components/ExportButton';

interface Summary {
  period: string;
  startDate: string;
  endDate: string;
  totalShipments: number;
  totalRevenue: number;
  totalCod: number;
  avgShipmentValue: number;
  delivered: number;
  inTransit: number;
  pending: number;
  previousPeriod: { totalShipments: number; totalRevenue: number };
  growth: { shipments: number; revenue: number };
}

interface DelegatePerformance {
  id: number;
  name: string;
  city: string;
  totalShipments: number;
  delivered: number;
  inTransit: number;
  totalCod: number;
  rating: number | null;
}

interface DailyTrend {
  date: string;
  count: number;
  revenue: number;
  delivered: number;
}

interface CityStat {
  city: string;
  count: number;
  revenue: number;
  delivered: number;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

export default function ReportsPage() {
  const [period, setPeriod] = useState('month');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [delegates, setDelegates] = useState<DelegatePerformance[]>([]);
  const [dailyTrend, setDailyTrend] = useState<DailyTrend[]>([]);
  const [cityStats, setCityStats] = useState<CityStat[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAllData();
  }, [period]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [summaryRes, delegatesRes, trendRes, cityRes] = await Promise.all([
        api.get(`/api/reports/summary?period=${period}`),
        api.get(`/api/reports/delegates-performance?period=${period}`),
        api.get(`/api/reports/daily-trend?days=30`),
        api.get(`/api/reports/city-stats?period=${period}`)
      ]);

      if (summaryRes.data.success) setSummary(summaryRes.data.data);
      if (delegatesRes.data.success) setDelegates(delegatesRes.data.data);
      if (trendRes.data.success) setDailyTrend(trendRes.data.data);
      if (cityRes.data.success) setCityStats(cityRes.data.data);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportData = [
    ...delegates.map(d => ({
      'المندوب': d.name,
      'المدينة': d.city,
      'الشحنات': d.totalShipments,
      'تم تسليمها': d.delivered,
      'قيد التوصيل': d.inTransit,
      'COD': d.totalCod,
      'التقييم': d.rating || '-'
    }))
  ];

  const exportColumns = [
    { key: 'المندوب', label: 'المندوب' },
    { key: 'المدينة', label: 'المدينة' },
    { key: 'الشحنات', label: 'الشحنات' },
    { key: 'تم تسليمها', label: 'تم تسليمها' },
    { key: 'COD', label: 'COD' },
    { key: 'التقييم', label: 'التقييم' }
  ];

  const formatCurrency = (value: number) => `${value.toLocaleString('ar-EG', { maximumFractionDigits: 2 })} ج.م`;
  const formatDate = (date: string) => new Date(date).toLocaleDateString('ar-EG', { day: '2-digit', month: '2-digit' });

  return (
    <div className="p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            التقارير المتقدمة
          </h1>
          <p className="text-muted-foreground mt-1">تحليل شامل للأداء والإدارة</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2 border rounded-lg bg-white"
          >
            <option value="week">أسبوع</option>
            <option value="month">شهر</option>
            <option value="quarter">ربع سنوي</option>
            <option value="year">سنة</option>
          </select>
          <ExportButton data={exportData} filename="reports" columns={exportColumns} title="التقارير" />
          <Button onClick={loadAllData} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ml-2 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Package className="w-8 h-8 text-blue-500" />
                <Badge className={summary.growth.shipments >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                  {summary.growth.shipments >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {Math.abs(summary.growth.shipments)}%
                </Badge>
              </div>
              <p className="text-sm text-blue-700">إجمالي الشحنات</p>
              <p className="text-2xl font-bold text-blue-900">{summary.totalShipments}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-8 h-8 text-green-500" />
                <Badge className={summary.growth.revenue >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                  {summary.growth.revenue >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {Math.abs(summary.growth.revenue)}%
                </Badge>
              </div>
              <p className="text-sm text-green-700">الإيرادات</p>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(summary.totalRevenue)}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Truck className="w-8 h-8 text-orange-500" />
              </div>
              <p className="text-sm text-orange-700">قيد التوصيل</p>
              <p className="text-2xl font-bold text-orange-900">{summary.inTransit}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle2 className="w-8 h-8 text-purple-500" />
              </div>
              <p className="text-sm text-purple-700">تم التسليم</p>
              <p className="text-2xl font-bold text-purple-900">{summary.delivered}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Daily Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              الاتجاه اليومي
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dailyTrend}>
                <defs>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={formatDate} />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Area yAxisId="left" type="monotone" dataKey="count" name="الشحنات" stroke="#3b82f6" fill="url(#colorCount)" />
                <Area yAxisId="right" type="monotone" dataKey="revenue" name="الإيرادات" stroke="#10b981" fill="url(#colorRevenue)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* City Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-red-500" />
              أفضل المدن
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={cityStats} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis dataKey="city" type="category" width={100} />
                <Tooltip formatter={(value: number) => value.toLocaleString('ar-EG')} />
                <Legend />
                <Bar dataKey="count" name="الشحنات" fill="#3b82f6" radius={[0, 8, 8, 0]} />
                <Bar dataKey="delivered" name="تم تسليمها" fill="#10b981" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Delegates Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-500" />
            أداء المندوبين
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-right p-3 font-semibold">المندوب</th>
                  <th className="text-right p-3 font-semibold">المدينة</th>
                  <th className="text-right p-3 font-semibold">الشحنات</th>
                  <th className="text-right p-3 font-semibold">تم تسليمها</th>
                  <th className="text-right p-3 font-semibold">قيد التوصيل</th>
                  <th className="text-right p-3 font-semibold">COD</th>
                  <th className="text-right p-3 font-semibold">التقييم</th>
                  <th className="text-right p-3 font-semibold">معدل النجاح</th>
                </tr>
              </thead>
              <tbody>
                {delegates.map((delegate) => {
                  const successRate = delegate.totalShipments > 0
                    ? Math.round((delegate.delivered / delegate.totalShipments) * 100)
                    : 0;
                  return (
                    <tr key={delegate.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-semibold">{delegate.name}</td>
                      <td className="p-3">
                        <Badge variant="outline">{delegate.city}</Badge>
                      </td>
                      <td className="p-3">{delegate.totalShipments}</td>
                      <td className="p-3">
                        <span className="text-green-600 font-semibold">{delegate.delivered}</span>
                      </td>
                      <td className="p-3">
                        <span className="text-orange-600 font-semibold">{delegate.inTransit}</span>
                      </td>
                      <td className="p-3">{formatCurrency(delegate.totalCod)}</td>
                      <td className="p-3">
                        {delegate.rating ? (
                          <div className="flex items-center gap-1">
                            <span className="text-yellow-500">★</span>
                            <span>{delegate.rating}</span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                successRate >= 80 ? 'bg-green-500' :
                                successRate >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${successRate}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold">{successRate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
